/* iter55z-audit round 9 — Sticky UTM capture.
 *
 * Reads `utm_source`, `utm_medium`, `utm_campaign` (+ common variants)
 * from the current URL. Persists the first-touch attribution in
 * `sessionStorage` so it survives modal navigation but never leaks
 * across browser sessions. Reads back via `readStickyUtms()` at
 * signup submission time.
 *
 * Design notes:
 *   - First-touch wins. Once we stamp a session, we don't overwrite
 *     it. A retargeted click that lands on /?utm_source=facebook
 *     after the visitor already touched the site via ?utm_source=google
 *     keeps the 'google' attribution.
 *   - `document.referrer` is the fallback when no explicit UTMs are on
 *     the URL — parses the hostname so 'twitter.com' becomes 't.co'-ish
 *     source cleanly.
 *   - Empty/direct visits are stored as `utm_source='direct'` so the
 *     backend never has to guess.
 *   - Oct-2026 — Reddit referrer hostnames (reddit.com, old.reddit.com,
 *     redd.it, etc.) are canonicalized to a single `'reddit'` channel so
 *     organic Reddit clicks group cleanly in the Marketing ROI tracker
 *     instead of fragmenting by hostname variant. `rdt_cid` (Reddit's
 *     own click-id param, appended to outbound links) is captured as
 *     `click_id` and, when present, also implies `utm_source='reddit'`
 *     — this is what lets us feed a SIGN_UP event back to Reddit's
 *     Conversions API at signup time.
 */
const STORAGE_KEY = 'pw:utm:v1';

const REFERRER_CHANNEL_MAP = {
  'reddit.com': 'reddit', 'old.reddit.com': 'reddit', 'new.reddit.com': 'reddit',
  'np.reddit.com': 'reddit', 'out.reddit.com': 'reddit', 'amp.reddit.com': 'reddit',
  'redd.it': 'reddit',
};

function _readFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search);
    const source   = p.get('utm_source')   || p.get('utm_src')   || '';
    const medium   = p.get('utm_medium')   || p.get('utm_med')   || '';
    const campaign = p.get('utm_campaign') || p.get('utm_camp')  || '';
    const clickId  = p.get('rdt_cid') || null;
    // iter-campaign-spend — `rc` is OUR OWN short-link code (see
    // `routes_click_tracker.py`), stamped on every `/api/r/{code}`
    // redirect. Lets a signup be joined back to the exact link that
    // drove it, so campaign spend logged against one specific short
    // link can compute its own CAC/LTV instead of only channel-wide.
    const refCode  = p.get('rc') || null;
    if (source || medium || campaign || clickId || refCode) {
      const normalizedSource = source.trim().toLowerCase();
      return {
        utm_source:   (REFERRER_CHANNEL_MAP[normalizedSource] || normalizedSource || (clickId ? 'reddit' : '')).slice(0, 64) || null,
        utm_medium:   medium.trim().slice(0, 64) || null,
        utm_campaign: campaign.trim().slice(0, 128) || null,
        click_id:     clickId ? clickId.slice(0, 128) : null,
        ref_code:     refCode ? refCode.slice(0, 32) : null,
      };
    }
  } catch (_) { /* URLSearchParams unavailable */ }
  return null;
}

function _fromReferrer() {
  try {
    if (!document.referrer) return null;
    const host = new URL(document.referrer).hostname.toLowerCase();
    if (!host || host === window.location.hostname) return null;
    // Strip 'www.' for cleaner grouping, then canonicalize known
    // Reddit domain variants down to a single 'reddit' channel.
    const src = host.replace(/^www\./, '');
    const canonical = REFERRER_CHANNEL_MAP[src] || src;
    return { utm_source: canonical.slice(0, 64), utm_medium: 'referral', utm_campaign: null, click_id: null, ref_code: null };
  } catch (_) { return null; }
}

/** First-touch capture. Safe to call on every render; idempotent. */
export function captureStickyUtms() {
  try {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const utms = _readFromUrl() || _fromReferrer() || {
      utm_source: 'direct', utm_medium: null, utm_campaign: null, click_id: null, ref_code: null,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utms));
  } catch (_) { /* private mode / no sessionStorage */ }
}

/** Read the sticky UTMs. Never throws. */
export function readStickyUtms() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) { /* ignore */ }
  // Last-chance capture: happens when the modal opens before landing
  // page ever calls captureStickyUtms (e.g. deep-linked signup URL).
  captureStickyUtms();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) { /* ignore */ }
  return { utm_source: 'direct', utm_medium: null, utm_campaign: null, click_id: null, ref_code: null };
}
